/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import styles from './styles.module.scss';
import dynamic from 'next/dynamic';
import root from 'window-or-global';
import { useEffect, useRef, useState } from 'react';
import { SubmitReviewButton } from '@/features/reviews/components/SubmitReviewButton/SubmitReviewButton';
import { type ProductModel } from '@/api/types/products-types';
import { Actionable, View } from '@az/starc-ui';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { scrollToCustomerReviews } from '../../utils/scrollToTitle';
import { ReviewStats } from '../ReviewStats/ReviewStats';
import type { ReviewStatistic } from '../../interface/types';
import { roundValueToPrecision } from '@/components/Rating/utils';
import { type ProductAnalyticsData } from '../../interface/types';
import { clickTrack } from '@/utils/analytics/clickTrack';

const LazyOverviewPopup = dynamic(() =>
  import('../ReviewsOverview/OverviewPopup/OverviewPopup').then((mod) => mod.OverviewPopup)
);

type ProductStatisticsProps = {
  productDetails: ProductModel | undefined;
  reviewStatistic: ReviewStatistic | undefined;
  reviewsPending?: boolean;
};

type ReviewAnalyticsData = {
  rating: number;
  reviewCount: number;
  productAnalyticsData?: ProductAnalyticsData;
};

const trackReviewLinkPDP = ({ rating, reviewCount, productAnalyticsData }: ReviewAnalyticsData) => {
  const { pageName = '', pageType = '', siteSection = '' } = root.utag_data ?? {};

  clickTrack({
    eventType: 'reviewsLinkInteractionPDP',
    customLinkName: `reviewsInteraction - ${pageType}`,
    pageName,
    pageType,
    siteSection,
    productAverageRating: rating,
    productReviewCount: reviewCount,
    ...productAnalyticsData,
  });
};

export const ProductStatistics = ({
  productDetails,
  reviewStatistic,
  reviewsPending,
}: ProductStatisticsProps) => {
  const isDesktop = useMediaQuery((theme) => theme.breakpoints.up('lg'));
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  const writeAReviewButton = useRef<HTMLButtonElement>(null);
  const anchor = useRef(null);

  const hasReviews = reviewStatistic && Number(reviewStatistic.totalReviewCount) > 0;

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const popper = document.getElementById('product-reviews-popper');
      if (popper && !popper.contains(event.target as Node)) {
        setIsPopupOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleClick = () => {
    if (isDesktop) {
      setIsPopupOpen((isOpen) => !isOpen);
    } else {
      scrollToCustomerReviews();
    }

    // Prepare analytics data
    const averageRating = reviewStatistic?.averageOverallRating || '0';
    const rating = roundValueToPrecision(Number(averageRating), 0.1);
    const reviewCount = Number(reviewStatistic?.totalReviewCount || '0');

    const productAnalyticsData: ProductAnalyticsData = {
      originalPartTypeIdList: productDetails?.originalPartTypeId ?? '',
      productIdList: [productDetails?.itemId ?? ''],
      productBrandList: [productDetails?.brandName ?? ''],
      productPartNumberList: productDetails?.partNumber ?? '',
      productRepositoryIdList: productDetails?.ecommProductId ?? '',
    };

    trackReviewLinkPDP({
      rating,
      reviewCount,
      productAnalyticsData,
    });
  };

  return (
    <>
      {isPopupOpen ? (
        <LazyOverviewPopup
          handleClose={() => setIsPopupOpen(false)}
          anchor={anchor}
          reviewStatistic={reviewStatistic}
          writeAReviewButton={writeAReviewButton}
        />
      ) : null}
      {reviewsPending ? <View className={styles.statisticsContainer} /> : null}
      {!reviewsPending && hasReviews ? (
        <View className={styles.statisticsContainer}>
          <Actionable onClick={handleClick} ref={anchor}>
            <ReviewStats reviewStatistic={reviewStatistic} />
          </Actionable>
        </View>
      ) : null}
      <div hidden>
        <SubmitReviewButton
          ref={writeAReviewButton}
          productId={productDetails?.itemId}
          productImage={productDetails?.itemImageUrl}
          productTitle={productDetails?.itemDescription}
          pageType="ProductDetail"
          label="Write review"
          variant="primary"
        />
      </div>
    </>
  );
};
